import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, useAuth } from '../../App';
import { CalendarDays, Coins, PawPrint, Bell, ArrowRight, Plus } from 'lucide-react';

export default function PawsDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiFetch('/api/bookings'),
      apiFetch('/api/pets'),
    ]).then(([b, p]) => { setBookings(b); setPets(p); setLoading(false); })
    .catch(() => setLoading(false));
  }, []);

  const upcoming = bookings.filter(b => ['CONFIRMED', 'REQUESTED'].includes(b.status));
  const completed = bookings.filter(b => b.status === 'COMPLETED').length;

  return (
    <div data-testid="paws-dashboard" className="max-w-4xl">
      <h1 className="font-outfit text-2xl sm:text-3xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>
        Welcome back, {user?.name?.split(' ')[0]}!
      </h1>
      <p className="text-sm mb-8" style={{ color: 'var(--text-secondary)' }}>Here's what's happening with your pets</p>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Upcoming', value: upcoming.length, icon: CalendarDays, color: 'petfolk-orange' },
          { label: 'Credits', value: `₹${(user?.creditBalance || 0).toFixed(0)}`, icon: Coins, color: 'petfolk-teal' },
          { label: 'My Pets', value: pets.length, icon: PawPrint, color: 'petfolk-purple' },
          { label: 'Completed', value: completed, icon: Bell, color: 'petfolk-orange' },
        ].map((s, i) => (
          <div key={i} className="petfolk-card p-5" data-testid={`stat-${s.label.toLowerCase()}`}>
            <div className="flex items-center justify-between mb-3">
              <s.icon size={20} style={{ color: `var(--${s.color})` }} />
            </div>
            <p className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{s.value}</p>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3 mb-8">
        <Link to="/bandra" className="btn-primary text-sm" data-testid="quick-book">Book Now <ArrowRight size={14} /></Link>
        <Link to="/paws/pets" className="btn-outline text-sm" data-testid="quick-add-pet"><Plus size={14} /> Add Pet</Link>
        <Link to="/paws/support" className="btn-outline text-sm" data-testid="quick-support">Contact Support</Link>
      </div>

      {/* Upcoming Bookings */}
      <div className="mb-8">
        <h2 className="font-outfit text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Upcoming Bookings</h2>
        {loading ? (
          <div className="space-y-3">{[1,2].map(i => <div key={i} className="skeleton h-20 rounded-2xl" />)}</div>
        ) : upcoming.length === 0 ? (
          <div className="petfolk-card p-8 text-center">
            <p style={{ color: 'var(--text-secondary)' }}>No upcoming bookings</p>
            <Link to="/bandra" className="btn-primary mt-3 text-sm inline-flex">Book Now</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {upcoming.map(b => (
              <div key={b.id} className="petfolk-card p-4 flex items-center gap-4" data-testid={`upcoming-${b.id}`}>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-petfolk-orange/20 to-petfolk-purple/10 flex items-center justify-center text-xl shrink-0">
                  {b.pet?.species === 'DOG' ? '🐕' : '🐈'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-outfit font-bold text-sm truncate" style={{ color: 'var(--text-primary)' }}>
                    {b.service?.name} for {b.pet?.name}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {b.node?.displayName} • {b.scheduledStart ? new Date(b.scheduledStart).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Pending schedule'}
                  </p>
                </div>
                <span className={`badge badge-${b.status.toLowerCase().replace('_', '-')}`}>{b.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pets */}
      <div>
        <h2 className="font-outfit text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>My Pets</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {pets.map(p => (
            <Link key={p.id} to="/paws/pets" className="petfolk-card p-4 flex items-center gap-4" data-testid={`pet-card-${p.id}`}>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-petfolk-orange/20 to-petfolk-purple/10 flex items-center justify-center text-2xl shrink-0">
                {p.species === 'DOG' ? '🐕' : '🐈'}
              </div>
              <div>
                <p className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>{p.name}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{p.breed || p.species} {p.size && `• ${p.size}`}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
